import React, { useState, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';

import { switchDataSet } from '@redux/actions/AuthActions';
import { makeStyles, withStyles } from '@material-ui/core/styles';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';

import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Button from '@material-ui/core/Button';
import SelectComponent from './Select';
import CollapsibleTable from './CollapsibleTable';
import {
  LEVERAGE_RETURN_ON_CAPITAL_A,
  LEVERAGE_RETURN_ON_CAPITAL_B,
  GROWTH_PROFITABILITY_ANALYSIS_A,
  GROWTH_PROFITABILITY_ANALYSIS_B,
  CREDIT_RATIOS_A,
  CREDIT_RATIOS_B
} from './fakedata2';
import SendModal from './SendModal';
import ExportButton from './ExportButton';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '20px 0'
  },
  table: {
    minWidth: 700
  },
  tableContainer: {
    borderTop: '5px solid #2DB9C4'
  },
  header: {
    padding: '20px',
    display: 'flex',
    alignItems: 'center',

    '& p': {
      marginLeft: 20,
      fontSize: 12
    },
    '& h2': {
      fontSize: 20,
      fontWeight: 'bold'
    }
  },
  input: {
    maxWidth: 75,
    width: 75,
    minHeight: 30,
    padding: 10,
    textAlign: 'center',
    border: '2px solid #002380',
    borderRadius: '5px',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0
    }
  },
  buttonDiv: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  rightButtons: {
    display: 'flex',
    alignItems: 'center'
  },
  send: {
    fontFamily: 'PT sans',
    fontSize: '16px',
    textTransform: 'lowercase',
    fontWeight: 'bold',
    borderRadius: 0,
    minWidth: '145px',
    marginLeft: '30px'
  },
  save: {
    background: '#6D7278',
    fontFamily: 'PT sans',
    fontSize: '16px',
    color: '#FFF',
    textTransform: 'lowercase',
    fontWeight: 'bold',
    borderRadius: 0,
    minWidth: '145px'
  }
}));

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white
  },
  body: {
    borderBottom: 'none !important',
    fontSize: 12
  }
}))(TableCell);

const StyledTableCellStrong = withStyles((theme) => ({
  head: {
    fontWeight: 'bold'
  },
  body: {
    fontSize: 12,
    fontWeight: 'bold',
    borderBottom: 'none !important'
  }
}))(TableCell);

const TableCellTitleStrong = withStyles((theme) => ({
  body: {
    fontSize: 14,
    fontWeight: 'bold',
    borderBottom: 'none !important'
  }
}))(TableCell);

const TableCellTitle = withStyles((theme) => ({
  body: {
    fontSize: 14,
    borderBottom: 'none !important'
  }
}))(TableCell);

const AnalyticRatio = ({ dataSet }) => {
  const outputs = useSelector((state) => state.FinancialModelReducer.outputs);
  const [leverage, setLeverage] = useState(LEVERAGE_RETURN_ON_CAPITAL_A);
  const [growth, setGrowth] = useState(GROWTH_PROFITABILITY_ANALYSIS_A);
  const [credit, setCredit] = useState(CREDIT_RATIOS_A);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const classes = useStyles();

  useEffect(() => {
    setLeverage(
      dataSet === 'dataA'
        ? LEVERAGE_RETURN_ON_CAPITAL_B
        : LEVERAGE_RETURN_ON_CAPITAL_A
    );
    setGrowth(
      dataSet === 'dataA'
        ? GROWTH_PROFITABILITY_ANALYSIS_B
        : GROWTH_PROFITABILITY_ANALYSIS_A
    );
    setCredit(dataSet === 'dataA' ? CREDIT_RATIOS_B : CREDIT_RATIOS_A);
  }, [dataSet]);

  return (
    <>
      {sendModalOpen && (
        <SendModal
          onClose={() => setSendModalOpen(false)}
          open={sendModalOpen}
        />
      )}
      <h5>You can build your own based on different sources of data</h5>
      <div className={classes.container}>
        {/* header */}
        <SelectComponent />
      </div>

      {/* GROWTH & PROFITABILITY ANALYSIS */}
      <CollapsibleTable
        borderColor="#2DB9C4"
        title="Growth & Profitability Analysis">
        <Table
          aria-label="customized table"
          className={classes.table}
          size="small">
          <TableHead>
            <TableRow>
              <StyledTableCellStrong />
              <StyledTableCellStrong align="center">2019</StyledTableCellStrong>
              <StyledTableCellStrong align="center">
                2020E
              </StyledTableCellStrong>
              <StyledTableCellStrong align="center">
                2021E
              </StyledTableCellStrong>
              <StyledTableCellStrong align="center">
                2022E
              </StyledTableCellStrong>
              <StyledTableCellStrong align="center">
                2023E
              </StyledTableCellStrong>
              <StyledTableCellStrong align="center">
                2024E
              </StyledTableCellStrong>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCellTitleStrong />
            </TableRow>
            <TableRow>
              <TableCellTitle>Revenues growth</TableCellTitle>
              {Object.values(outputs.revenues_growth)
                .slice(1)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitle>EBITDA growth</TableCellTitle>
              {Object.values(outputs.ebitda_growth)
                .slice(1)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitle>EBITA growth</TableCellTitle>
              {Object.values(outputs.ebita_growth)
                .slice(1)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitle>Net income growth</TableCellTitle>
              {Object.values(outputs.net_income_growth)
                .slice(1)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitleStrong />
              {growth &&
                growth[4].map((val) => (
                  <StyledTableCell align="center">{val}</StyledTableCell>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitle>Revenues CAGR 2019-2023</TableCellTitle>
              {Object.values(outputs.revenues_cagr)
                .slice(1, 2)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitle>Revenues CAGR 2020-2023</TableCellTitle>
              {Object.values(outputs.revenues_cagr)
                .slice(2, 3)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitle>EBITDA CAGR 2020-2023</TableCellTitle>
              {Object.values(outputs.ebitda_cagr)
                .slice(1, 2)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitle>EBITDA CAGR 2020-2023</TableCellTitle>
              {Object.values(outputs.ebitda_cagr)
                .slice(2, 3)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitleStrong />
              {growth &&
                growth[9].map((val) => (
                  <StyledTableCell align="center">{val}</StyledTableCell>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitle>EBITDA margin</TableCellTitle>
              {Object.values(outputs.ebitda_margin)
                .slice(1)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
          </TableBody>
        </Table>
      </CollapsibleTable>

      {/* CREDIT RATIO */}
      <CollapsibleTable borderColor="#FFCD00" title="Credit ratios">
        <Table
          aria-label="customized table"
          className={classes.table}
          size="small">
          <TableHead>
            <TableRow>
              <StyledTableCellStrong />
              <StyledTableCellStrong align="center">2019</StyledTableCellStrong>
              <StyledTableCellStrong align="center">
                2020E
              </StyledTableCellStrong>
              <StyledTableCellStrong align="center">
                2021E
              </StyledTableCellStrong>
              <StyledTableCellStrong align="center">
                2022E
              </StyledTableCellStrong>
              <StyledTableCellStrong align="center">
                2023E
              </StyledTableCellStrong>
              <StyledTableCellStrong align="center">
                2024E
              </StyledTableCellStrong>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCellTitleStrong />
            </TableRow>
            <TableRow>
              <TableCellTitle>Unlevered FCF / Operating profit</TableCellTitle>
              {Object.values(outputs.unlevered_fcf_op_profit)
                .slice(1)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitle>Unlevered FCF / Reported EBITDA</TableCellTitle>
              {Object.values(outputs.unlevered_fcf_ebitda)
                .slice(1)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitleStrong />
              {credit &&
                credit[2].map((val) => (
                  <StyledTableCell align="center">{val}</StyledTableCell>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitle>Net financial debt/Adj. EBITDA</TableCellTitle>
              {Object.values(outputs.net_fin_debt_adj_ebitda)
                .slice(1)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitle>
                Net financial debt/(Adj. EBITDA - Capex)
              </TableCellTitle>
              {Object.values(outputs.net_fin_debt_adj_ebitda_capex)
                .slice(1)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitle>Adj. EBITDA/Net interest</TableCellTitle>
              {Object.values(outputs.adj_ebitda_net_interest)
                .slice(1)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitle>Dividend / FCF (to equity)</TableCellTitle>
              {Object.values(outputs.div_fcf_equity)
                .slice(1)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
          </TableBody>
        </Table>
      </CollapsibleTable>
      {/* LEVERAGE & RETURN ON CAPITAL */}
      <CollapsibleTable
        borderColor="#CD0032"
        title="Leverage & return on capital">
        <Table
          aria-label="customized table"
          className={classes.table}
          size="small">
          <TableHead>
            <TableRow>
              <StyledTableCellStrong />
              <StyledTableCellStrong align="center">2019</StyledTableCellStrong>
              <StyledTableCellStrong align="center">
                2020E
              </StyledTableCellStrong>
              <StyledTableCellStrong align="center">
                2021E
              </StyledTableCellStrong>
              <StyledTableCellStrong align="center">
                2022E
              </StyledTableCellStrong>
              <StyledTableCellStrong align="center">
                2023E
              </StyledTableCellStrong>
              <StyledTableCellStrong align="center">
                2024E
              </StyledTableCellStrong>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCellTitleStrong>Leverage analysis</TableCellTitleStrong>
            </TableRow>
            <TableRow>
              <TableCellTitle>Net financial debt / equity</TableCellTitle>
              {Object.values(outputs.net_fin_debt_equity)
                .slice(1)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitle>
                Net financial debt / Equity + net financial debt
              </TableCellTitle>
              {Object.values(outputs.net_fin_debt_equity_net_fin_debt)
                .slice(1)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitleStrong>Return on Capital</TableCellTitleStrong>
            </TableRow>
            <TableRow>
              <TableCellTitle>ROCE</TableCellTitle>
              {Object.values(outputs.roce)
                .slice(1)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitle>Cash ROCE</TableCellTitle>
              {Object.values(outputs.cash_roce)
                .slice(1)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
            <TableRow>
              <TableCellTitle>ROE</TableCellTitle>
              {Object.values(outputs.roe)
                .slice(1)
                .map((val) => (
                  <StyledTableCellStrong align="center">
                    {val}
                  </StyledTableCellStrong>
                ))}
            </TableRow>
          </TableBody>
        </Table>
      </CollapsibleTable>
      <div className={classes.buttonDiv}>
        <div>
          <Button className={classes.save} variant="contained">
            save
          </Button>
        </div>
        <div className={classes.rightButtons}>
          <ExportButton />
          <Button
            className={classes.send}
            color="primary"
            onClick={() => {}}
            variant="contained">
            send to issuer
          </Button>
        </div>
      </div>
    </>
  );
};

export const mapStateToProps = (state) => ({
  dataSet: state.AuthReducer.dataSet
});

export const mapDispatchToProps = (dispatch) => {
  return {
    switchData: () => dispatch(switchDataSet())
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AnalyticRatio);
